/**
 * Runner finish-time percentile distributions.
 *
 * All cutoff times are transcribed verbatim from the RunRepeat "Runner's
 * Percentile Calculator" study (Andersen, 2017), based on ~35 million race
 * results from 28,000+ races (1996–2016), elites excluded.
 *
 * Source: https://runrepeat.com/how-do-you-masure-up-the-runners-percentile-calculator
 *
 * Convention: the P-th percentile time is the time at or below which the
 * fastest P% of runners finish. A LOWER time = a HIGHER percentile rank.
 * P1 is elite-fast; P90 is the back of the recreational pack.
 *
 * Distributions are only published for 5K / 10K / half / marathon and for
 * overall / men / women. Lookup returns null for other combinations (no
 * 1500m/mile/3K, no age groups in the static tables) — the UI surfaces a
 * note rather than fabricating a number.
 */

import type { PercentilePoint, RaceDistance, RunnerCohort } from '../shared/types';

/** Percentile grid, fastest → slowest: P1, P10, P20 … P90. */
const P_GRID = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90] as const;

/** A 10-element row of finish times in seconds, aligned to P_GRID. */
type DistRow = readonly [
  number, number, number, number, number,
  number, number, number, number, number,
];

// Verbatim RunRepeat cutoffs, converted HH:MM:SS → seconds.
// (machine-generated & monotonicity-checked against the source tables.)
const FIVE_K_OVERALL: DistRow = [1120, 1520, 1693, 1826, 1949, 2077, 2218, 2388, 2619, 3004];
const FIVE_K_MEN: DistRow = [1050, 1406, 1564, 1678, 1781, 1888, 2008, 2155, 2361, 2743];
const FIVE_K_WOMEN: DistRow = [1299, 1704, 1869, 1999, 2121, 2248, 2387, 2556, 2783, 3144];

const TEN_K_OVERALL: DistRow = [2178, 2891, 3167, 3363, 3545, 3728, 3938, 4208, 4605, 5278];
const TEN_K_MEN: DistRow = [2064, 2711, 2977, 3148, 3290, 3435, 3602, 3823, 4153, 4761];
const TEN_K_WOMEN: DistRow = [2472, 3215, 3481, 3662, 3830, 4014, 4238, 4536, 4966, 5594];

const HALF_OVERALL: DistRow = [5039, 6430, 6972, 7368, 7734, 8099, 8502, 8988, 9665, 10758];
const HALF_MEN: DistRow = [4717, 6035, 6553, 6903, 7096, 7188, 7798, 8191, 8757, 9768];
const HALF_WOMEN: DistRow = [5755, 7021, 7558, 7945, 8290, 8643, 9044, 9545, 10231, 11301];

const MARATHON_OVERALL: DistRow = [10248, 12706, 13793, 14576, 15309, 15993, 16710, 17538, 18660, 20505];
const MARATHON_MEN: DistRow = [9858, 12160, 13213, 13987, 14627, 15269, 15933, 16706, 17733, 19526];
const MARATHON_WOMEN: DistRow = [11495, 13762, 14818, 15624, 16281, 16929, 17617, 18456, 19600, 21391];

/** Full dataset. `null`-via-absence marks unsupported (distance, cohort) pairs. */
const DISTRIBUTIONS: Partial<Record<RaceDistance, Partial<Record<RunnerCohort, DistRow>>>> = {
  '5K': { overall: FIVE_K_OVERALL, men: FIVE_K_MEN, women: FIVE_K_WOMEN },
  '10K': { overall: TEN_K_OVERALL, men: TEN_K_MEN, women: TEN_K_WOMEN },
  half: { overall: HALF_OVERALL, men: HALF_MEN, women: HALF_WOMEN },
  marathon: { overall: MARATHON_OVERALL, men: MARATHON_MEN, women: MARATHON_WOMEN },
};

/** Distances for which we hold population distributions. */
export const PERCENTILE_DISTANCES: RaceDistance[] = ['5K', '10K', 'half', 'marathon'];

/** Cohorts available for selection in the UI. */
export const PERCENTILE_COHORTS: RunnerCohort[] = ['overall', 'men', 'women'];

/** Human label for each cohort. */
export const COHORT_LABELS: Record<RunnerCohort, string> = {
  overall: 'All runners',
  men: 'Men',
  women: 'Women',
};

/** Lowercase distance name for prose, e.g. "marathon", "half-marathon". */
export const RACE_DISTRIBUTION_NAMES: Record<RaceDistance, string> = {
  '1500m': '1500 m',
  '1mile': 'mile',
  '3K': '3K',
  '5K': '5K',
  '10K': '10K',
  half: 'half-marathon',
  marathon: 'marathon',
};

/** Is a percentile distribution available for this distance + cohort? */
export function hasDistribution(distance: RaceDistance, cohort: RunnerCohort): boolean {
  return Boolean(DISTRIBUTIONS[distance]?.[cohort]);
}

/**
 * Return the percentile distribution for a distance + cohort as a list of
 * {percentile, timeSeconds}, ordered fastest (P1) → slowest (P90).
 * Returns null if no data exists for the combination.
 */
export function getDistribution(
  distance: RaceDistance,
  cohort: RunnerCohort,
): PercentilePoint[] | null {
  const row = DISTRIBUTIONS[distance]?.[cohort];
  if (!row) return null;
  const points: PercentilePoint[] = [];
  for (let i = 0; i < P_GRID.length; i++) {
    const p = P_GRID[i];
    const time = row[i];
    if (p !== undefined && typeof time === 'number') {
      points.push({ percentile: p, timeSeconds: time });
    }
  }
  return points;
}
